import UpdateAdvancedTimeRegistrationContent from '../../../my-work-tab/my-timesheets-page/bottom-section/timesheets-time-registration-table/advanced-time-registration-input/UpdateAdvancedTimeRegistrationContent';
import ForecastTooltip from '../tooltips/ForecastTooltip';
import React, {forwardRef, ReactElement} from 'react';
import styled from 'styled-components';
import {Icon, PopoverHandler} from '@forecast-it/design-system';
import {hasNoAccessToTimeRegProject} from '../../../../components/timesheets/team-timesheet/TimesheetsUtil';

import {IntlShape, useIntl} from 'react-intl';
import {TimeRegistration} from '../../../my-work-tab/my-timesheets-page/bottom-section/timesheets-time-registration-table/TimeRegistrationType';
import {TimeRegistrationNode} from './EditTimeEntriesLogic';

function formatDayRegs(dayRegs: TimeRegistrationNode[], intl: IntlShape): TimeRegistration[] {
	return dayRegs.map(reg => {
		return {
			id: reg.node.id,
			minutesRegistered: reg.node.minutesRegistered,
			billableMinutesRegistered: reg.node.billableMinutesRegistered,
			notes: reg.node.notes ? reg.node.notes : '',
			role: reg.node.role,
			noteInvalid: false,
			shouldUpdate: true,
			idleTimeId: reg.node?.idleTimeId || reg.node?.idleTime?.id,
			canHaveRole: !reg.node?.idleTimeId && !reg.node?.idleTime?.id,
			taskEstimateForecastMinutes: reg?.node?.task ? reg.node.task.estimateForecastMinutes : null,
			taskTotalMinutesRegistered: reg?.node?.task ? reg.node.task.totalMinutesRegistered : null,
			taskId: reg.node.task ? reg.node.task.id : undefined,
			billable: reg.node.billable,
			fullAccessToProject: !hasNoAccessToTimeRegProject(reg.node),
			isTimeRegLocked:
				!!reg.node.hiBobAllocationId ||
				(!!reg.node.allocationId && !!reg.node.idleTime && !reg.node.idleTime.isInternalTime),
			tooltipMessage: reg.node.hiBobAllocationId ? intl.formatMessage({id: 'integrations.hibob.time_off_tooltip'}) : '',
		};
	});
}

function getProjectIdFromTimeRegs(dayRegs: TimeRegistrationNode[]) {
	return dayRegs[0]?.node?.project?.id || dayRegs[0]?.node?.task?.project?.id;
}

const EditTimeEntriesStyle = styled.div<{showBillableTime: boolean}>`
	margin: ${props => (props.showBillableTime ? '8px' : '19px')} 4px;
	z-index: 2;
`;

type ToggleEditButtonProps = {
	children: ReactElement;
	disabled: boolean | undefined;
};

const ToggleEditButton = forwardRef<HTMLDivElement, ToggleEditButtonProps>(({children, disabled}, ref) => {
	const {open, setOpen} = PopoverHandler.usePopoverState();
	const toggle = () => setOpen(!open);

	return (
		<div onClick={disabled ? undefined : toggle} className={'edit-button' + (open ? '-visible' : '')} ref={ref}>
			{children}
		</div>
	);
});

interface EditTimeRegMutationParameters {
	hours: number;
	billableHours: number;
	dayRegs: TimeRegistrationNode[];
	selectedPerson;
	notes: string;
	timeRegId: string;
	roleId: string;
	currentViewingDate: Date;
	intl: IntlShape;
	onSuccess: () => void;
}

interface EditTimeEntriesProps {
	dayRegNodes: TimeRegistrationNode[];
	dayOfInput: Date;
	infoText?: string;
	disabled?: boolean;
	mutation: (parameters: EditTimeRegMutationParameters) => void; //function to be called when the user clicks the save
	showBillableTime: boolean;
}
const EditTimeEntries = ({
	dayRegNodes,
	dayOfInput,
	infoText,
	disabled,
	mutation,
	showBillableTime,
}: EditTimeEntriesProps): ReactElement | null => {
	const selectedPerson = dayRegNodes[0]?.node?.person;
	const intl = useIntl();

	if (!selectedPerson) {
		return null;
	}

	function mutationWrapper(hours, billableHours, notes, timeRegId, roleId, onSuccess) {
		mutation({
			hours,
			billableHours,
			dayRegs: dayRegNodes,
			selectedPerson,
			notes,
			timeRegId,
			roleId,
			currentViewingDate: dayOfInput,
			intl,
			onSuccess,
		});
	}

	return (
		<EditTimeEntriesStyle showBillableTime={showBillableTime} onClick={e => e.stopPropagation()}>
			<PopoverHandler>
				<PopoverHandler.Trigger asChild={true}>
					<ToggleEditButton disabled={disabled}>
						<ForecastTooltip content={infoText} maxWidth={250}>
							<Icon data-cy={'edit-time-reg-button'} icon={'edit'} color={disabled ? 'disabled' : 'default'} />
						</ForecastTooltip>
					</ToggleEditButton>
				</PopoverHandler.Trigger>
				<PopoverHandler.Content showArrow={true}>
					<UpdateAdvancedTimeRegistrationContent
						timeRegistrationsOnEntityOnDay={formatDayRegs(dayRegNodes, intl)}
						mutation={mutationWrapper}
						selectedPerson={selectedPerson}
						dayOfInput={dayOfInput}
						projectId={getProjectIdFromTimeRegs(dayRegNodes)}
						isCompanyTimesheet={true}
					/>
				</PopoverHandler.Content>
			</PopoverHandler>
		</EditTimeEntriesStyle>
	);
};

export default EditTimeEntries;
